import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import { Trans, useTranslation } from 'react-i18next';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { GetStaticProps } from 'next/types';
import { Layout } from '@/components/layout';
import { SeoHeader } from '@/components/SeoHeader';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { Facts } from '@/sections/cssProgram/Facts';
import Floating from '@/sections/Floating';
import ComparisonTable from '@/sections/cssProgram/ComparisonTable';
import { EshopsList, getAffiliateEshopsList } from '@/api/sanity/affiliateEshopsList';
import Eshops from '@/sections/Eshops';
import { Button } from '@/components/buttons';
import { Cards } from '@/sections/cssProgram/Cards';
import MoreInfoSection from '@/sections/MoreInfo';
import { LINKS } from '@/constants/links';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconSkippay from '@/assets/icons/heroIcons/Skippay.svg';
import { ContactForm } from '@/sections/cssProgram/ContactForm';
import { NumericRange } from '@/types/range';
import { FAQs } from '@/components/seo';
import { SectionHeadline } from '@/components/SectionHeadline';
import Hero from '@/sections/Hero';
import { useRef } from 'react';
import { Link } from '@/components/links';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
  eshops: EshopsList;
}
export default function Page(props) {
  const $ = _c(155);
  usePageView("referral");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["cssProgram", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const faqsRef = useRef(null);
  const contactRef = useRef(null);
  const {
    pageBanner,
    eshops,
    menuPromoBanners
  } = props;
  let t1;
  if ($[1] !== t) {
    t1 = Array.from({
      length: 12
    }, (_, index) => {
      const faqIndex = index + 1 as NumericRange<1, 12>;
      return {
        question: t(`cssProgram:faqs.questions.${faqIndex}.question`),
        answer: faqIndex === 5 ? <Trans t={t} i18nKey="cssProgram:faqs.questions.5.answer" components={{
          paragraph: <p className="mb-1" />
        }} /> : faqIndex === 3 ? <Trans t={t} i18nKey="cssProgram:faqs.questions.3.answer" components={{
          paragraph: <p className="mb-1" />,
          semibold: <p className="mb-1 font-semibold" />,
          link_pdf: <Link analyticsParam="cssProgramPromotion" testId="cssProgramPromotion" to="/files/cs/Skippay_Moznosti_propagace_pro-eshopy.pdf" target="_blank" />
        }} /> : t(`cssProgram:faqs.questions.${faqIndex}.answer`)
      };
    });
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const faqsData = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (faqsRef.current) {
        faqsRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleScrollToFaqs = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (contactRef.current) {
        contactRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleScrollToContact = t3;
  let t4;
  if ($[5] !== t) {
    t4 = t("seo.title");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("seo.description");
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <SeoHeader title={t4} description={t5} />;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== pageBanner?.title || $[13] !== t) {
    t7 = pageBanner?.title ?? t("cssProgram:banner.title");
    $[12] = pageBanner?.title;
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== pageBanner?.text || $[16] !== t) {
    t8 = pageBanner?.text ?? t("cssProgram:banner.text");
    $[15] = pageBanner?.text;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== pageBanner || $[19] !== t) {
    t9 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("cssProgram:banner.ctaPrimary"),
      label: t("cssProgram:banner.ctaPrimary"),
      onClick: handleScrollToContact,
      testId: "cssProgramHeroPrimaryCTA",
      analyticsParam: "cssProgramHeroPrimaryCTA",
      variant: "primary"
    }, {
      ariaLabel: t("cssProgram:banner.ctaSecondary"),
      label: t("cssProgram:banner.ctaSecondary"),
      onClick: handleScrollToFaqs,
      testId: "cssProgramHeroSecondaryCTA",
      analyticsParam: "cssProgramHeroSecondaryCTA",
      variant: "secondary"
    }];
    $[18] = pageBanner;
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = pageBanner?.image?.url ?? "/images/sections/cssProgram/hero/cs/hero.png";
  let t11;
  if ($[21] !== pageBanner?.image?.alt || $[22] !== t) {
    t11 = pageBanner?.image?.alt ?? t("cssProgram:banner.title");
    $[21] = pageBanner?.image?.alt;
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = {
      url: t10,
      alt: t11
    };
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = pageBanner?.variant || "mockupDesktop";
  let t14;
  if ($[27] !== t12 || $[28] !== t13 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t14 = <Hero title={t7} text={t8} buttons={t9} image={t12} variant={t13} />;
    $[27] = t12;
    $[28] = t13;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-900" background="bg-transparent" /></div>;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="relative bg-blue-900 py-14 lg:pt-20 lg:pb-40"><Facts onCtaClick={handleScrollToContact} /></div>;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t14) {
    t17 = <div className="relative overflow-x-hidden">{t14}{t15}{t16}</div>;
    $[35] = t14;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="bg-blue-900"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t) {
    t19 = t("cssProgram:bonuses.1.text");
    $[38] = t;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t) {
    t20 = t("cssProgram:bonuses.1.title");
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t) {
    t21 = t("cssProgram:bonuses.1.title");
    $[42] = t;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t21) {
    t22 = {
      alt: t21,
      src: "/images/sections/cssProgram/cs/brain-heart-purse.png"
    };
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t19 || $[47] !== t20 || $[48] !== t22) {
    t23 = {
      anchorKey: "cssProgram.bonuses.1.title",
      content: t19,
      title: t20,
      image: t22,
      variant: "small"
    };
    $[46] = t19;
    $[47] = t20;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t) {
    t24 = t("cssProgram:bonuses.2.text");
    $[50] = t;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t) {
    t25 = t("cssProgram:bonuses.2.title");
    $[52] = t;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t) {
    t26 = t("cssProgram:bonuses.2.title");
    $[54] = t;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t26) {
    t27 = {
      alt: t26,
      src: "/images/sections/cssProgram/cs/brain-heart-graph.png"
    };
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t24 || $[59] !== t25 || $[60] !== t27) {
    t28 = {
      anchorKey: "cssProgram.bonuses.2.title",
      content: t24,
      title: t25,
      image: t27,
      imagePosition: "right",
      variant: "small"
    };
    $[58] = t24;
    $[59] = t25;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t) {
    t29 = t("cssProgram:bonuses.3.text");
    $[62] = t;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t) {
    t30 = t("cssProgram:bonuses.3.title");
    $[64] = t;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t) {
    t31 = t("cssProgram:bonuses.3.title");
    $[66] = t;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== t31) {
    t32 = {
      alt: t31,
      src: "/images/sections/cssProgram/cs/brain-heart-notebook.png"
    };
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t29 || $[71] !== t30 || $[72] !== t32) {
    t33 = {
      anchorKey: "cssProgram.bonuses.3.title",
      content: t29,
      title: t30,
      image: t32,
      variant: "small"
    };
    $[70] = t29;
    $[71] = t30;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t23 || $[75] !== t28 || $[76] !== t33) {
    t34 = [t23, t28, t33];
    $[74] = t23;
    $[75] = t28;
    $[76] = t33;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== t) {
    t35 = t("cssProgram:bonuses.title");
    $[78] = t;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== t34 || $[81] !== t35) {
    t36 = <div className="pb-14 lg:pb-40"><Floating boxes={t34} title={t35} /></div>;
    $[80] = t34;
    $[81] = t35;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  let t37;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <ComparisonTable onCtaClick={handleScrollToContact} />;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t36) {
    t38 = <div className="relative bg-blue-100 pt-14 pb-20 lg:pt-20 lg:pb-40">{t36}{t37}</div>;
    $[84] = t36;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="bg-blue-100"><SectionDelimiter color="bg-blue-50" background="bg-transparent" /></div>;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== eshops || $[88] !== t) {
    t40 = eshops?.length > 0 && <Eshops eshops={eshops} title={t("cssProgram:eshops.title")} hideText={true} button={<Button align="center" variant="primary" size="md" onClick={handleScrollToContact} testId="cssProgramEshopsList" analyticsParam="cssProgramEshopsList" className="mt-12">{t("cssProgram:eshops.button")}</Button>} className="pb-20" />;
    $[87] = eshops;
    $[88] = t;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  let t41;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Cards onCtaClick={handleScrollToContact} />;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  let t42;
  if ($[91] !== t40) {
    t42 = <div className="bg-blue-50 pt-14 pb-20 lg:pt-20 lg:pb-40">{t40}{t41}</div>;
    $[91] = t40;
    $[92] = t42;
  } else {
    t42 = $[92];
  }
  let t43;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-700" background="bg-transparent" /></div>;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] !== t) {
    t44 = t("cssProgram:faqs.anchor");
    $[94] = t;
    $[95] = t44;
  } else {
    t44 = $[95];
  }
  let t45;
  if ($[96] !== t) {
    t45 = t("cssProgram:faqs.title");
    $[96] = t;
    $[97] = t45;
  } else {
    t45 = $[97];
  }
  let t46;
  if ($[98] !== t45) {
    t46 = <SectionHeadline className="mb-12 lg:mb-16" variant="inverted" anchorKey="cssProgram.faqs.title" id="faq">{t45}</SectionHeadline>;
    $[98] = t45;
    $[99] = t46;
  } else {
    t46 = $[99];
  }
  let t47;
  if ($[100] !== faqsData) {
    t47 = <FAQs data={faqsData} hideButton={true} className="pb-14 lg:pb-20" />;
    $[100] = faqsData;
    $[101] = t47;
  } else {
    t47 = $[101];
  }
  let t48;
  if ($[102] !== t44 || $[103] !== t46 || $[104] !== t47) {
    t48 = <div id={t44} ref={faqsRef}>{t46}{t47}</div>;
    $[102] = t44;
    $[103] = t46;
    $[104] = t47;
    $[105] = t48;
  } else {
    t48 = $[105];
  }
  let t49;
  if ($[106] !== t) {
    t49 = t("cssProgram:form.anchor");
    $[106] = t;
    $[107] = t49;
  } else {
    t49 = $[107];
  }
  let t50;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <ContactForm />;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  let t51;
  if ($[109] !== t49) {
    t51 = <div id={t49} ref={contactRef}>{t50}</div>;
    $[109] = t49;
    $[110] = t51;
  } else {
    t51 = $[110];
  }
  let t52;
  if ($[111] !== t48 || $[112] !== t51) {
    t52 = <div className="bg-blue-700 pt-14 pb-20 lg:pt-20 lg:pb-40"><div className="mx-auto max-w-192 px-4 lg:px-0">{t48}{t51}</div></div>;
    $[111] = t48;
    $[112] = t51;
    $[113] = t52;
  } else {
    t52 = $[113];
  }
  let t53;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <div className="bg-blue-700"><SectionDelimiter color="bg-blue-50" background="bg-transparent" /></div>;
    $[114] = t53;
  } else {
    t53 = $[114];
  }
  let t54;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <IconDeferredPayment className="fillButtonIcon" />;
    $[115] = t54;
  } else {
    t54 = $[115];
  }
  let t55;
  if ($[116] !== t) {
    t55 = t("core:link.deferredPayment");
    $[116] = t;
    $[117] = t55;
  } else {
    t55 = $[117];
  }
  let t56;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = LINKS();
    $[118] = t56;
  } else {
    t56 = $[118];
  }
  let t57;
  if ($[119] !== t55) {
    t57 = {
      analyticsParam: "cssProgramMoreInfoDeferredPayment",
      icon: t54,
      testId: "cssProgramMoreInfoDeferredPayment",
      title: t55,
      to: t56.deferredPayment
    };
    $[119] = t55;
    $[120] = t57;
  } else {
    t57 = $[120];
  }
  let t58;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <IconMaxi className="fillButtonIcon" />;
    $[121] = t58;
  } else {
    t58 = $[121];
  }
  let t59;
  if ($[122] !== t) {
    t59 = t("core:products.revolving");
    $[122] = t;
    $[123] = t59;
  } else {
    t59 = $[123];
  }
  let t60;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = LINKS();
    $[124] = t60;
  } else {
    t60 = $[124];
  }
  let t61;
  if ($[125] !== t59) {
    t61 = {
      analyticsParam: "cssProgramMoreInfoRevolvingApplication",
      icon: t58,
      testId: "cssProgramMoreInfoRevolvingApplication",
      title: t59,
      to: t60.revolving
    };
    $[125] = t59;
    $[126] = t61;
  } else {
    t61 = $[126];
  }
  let t62;
  if ($[127] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <IconThird className="fillButtonIcon" />;
    $[127] = t62;
  } else {
    t62 = $[127];
  }
  let t63;
  if ($[128] !== t) {
    t63 = t("core:products.payInThree");
    $[128] = t;
    $[129] = t63;
  } else {
    t63 = $[129];
  }
  let t64;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = LINKS();
    $[130] = t64;
  } else {
    t64 = $[130];
  }
  let t65;
  if ($[131] !== t63) {
    t65 = {
      analyticsParam: "cssProgramMoreInfoPayInThree",
      icon: t62,
      testId: "cssProgramMoreInfoPayInThree",
      title: t63,
      to: t64.payInThree
    };
    $[131] = t63;
    $[132] = t65;
  } else {
    t65 = $[132];
  }
  let t66;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <IconSkippay className="fillButtonIcon" />;
    $[133] = t66;
  } else {
    t66 = $[133];
  }
  let t67;
  if ($[134] !== t) {
    t67 = t("core:link.aboutUs");
    $[134] = t;
    $[135] = t67;
  } else {
    t67 = $[135];
  }
  let t68;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = LINKS();
    $[136] = t68;
  } else {
    t68 = $[136];
  }
  let t69;
  if ($[137] !== t67) {
    t69 = {
      analyticsParam: "cssProgramMoreInfoAboutUs",
      icon: t66,
      testId: "cssProgramMoreInfoAboutUs",
      title: t67,
      to: t68.aboutCompany
    };
    $[137] = t67;
    $[138] = t69;
  } else {
    t69 = $[138];
  }
  let t70;
  if ($[139] !== t57 || $[140] !== t61 || $[141] !== t65 || $[142] !== t69) {
    t70 = <div className="relative z-10"><MoreInfoSection buttons={[t57, t61, t65, t69]} /></div>;
    $[139] = t57;
    $[140] = t61;
    $[141] = t65;
    $[142] = t69;
    $[143] = t70;
  } else {
    t70 = $[143];
  }
  let t71;
  if ($[144] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[144] = t71;
  } else {
    t71 = $[144];
  }
  let t72;
  if ($[145] !== t70) {
    t72 = <div className="relative overflow-hidden bg-blue-50">{t70}{t71}</div>;
    $[145] = t70;
    $[146] = t72;
  } else {
    t72 = $[146];
  }
  let t73;
  if ($[147] !== menuPromoBanners || $[148] !== t17 || $[149] !== t38 || $[150] !== t42 || $[151] !== t52 || $[152] !== t6 || $[153] !== t72) {
    t73 = <Layout menuPromoBanners={menuPromoBanners}>{t6}{t17}{t18}{t38}{t39}{t42}{t43}{t52}{t53}{t72}</Layout>;
    $[147] = menuPromoBanners;
    $[148] = t17;
    $[149] = t38;
    $[150] = t42;
    $[151] = t52;
    $[152] = t6;
    $[153] = t72;
    $[154] = t73;
  } else {
    t73 = $[154];
  }
  return t73;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('css-program');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      eshops: await getAffiliateEshopsList('css-program'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanner: await getPageBannerBySlug('css-program'),
      ...(await getIntlProps(ctx, ['core', 'cssProgram']))
    }
  };
}) satisfies GetStaticProps<Props>;